/* eslint-disable react/no-danger */
import { useEffect, useState } from 'react';

import Head from 'next/head';
import { useRecoilState, useResetRecoilState } from 'recoil';

import { activateWarrantyWithSerialNumber, validateSerialNumber } from '../../../../apis/warranty-activation';
import DropDownSelect from '../../../../components/inputs/dropdownSelect';
import InputTextField from '../../../../components/inputs/inputTextField';
import WarrantyActivatedModalEmbed from '../../../../components/revamp/warranty-activated-modal-embed';
import { warrantyActivationFailed,
  warrantyActivationInitiatedClicked } from '../../../../helpers/warranty-activation-segment';
import useI18n from '../../../../hooks/use-i18n';
import { languages, contentLanguageMap, countryMap } from '../../../../language.config';
import warrantyInput from '../../../../recoil/atoms/warrantyInput';
import { OPTIONAL_WARRANTY_FIELDS, ORDERID_REQUIRED_COUNTRIES, WARRANTY_ACTIVATION_ERRORS,
  WARRANTY_ACTIVATION_EVENTS, WARRANTY_ACTIVATION_STATUS } from '../../../../utils/constants';
import getTranslationKeys, { PAGEID } from '../../../../utils/i18nRosseta';
import { purchaseFromOptions, productSerialNumberCodes,
  validateEmail, isValidName } from '../../../../utils/utils';

import '../../../../styles/layout.scss';
import '../../../../styles/globals.scss';
import '../../../../styles/revamp/warranty-activation-embed.scss';

const WarrantyActivation = () => {
  const i18n = useI18n();
  const codes = i18n.activeLocale.split('-');
  const country = codes.length === 2 ? codes[0] : 'sg';
  const lang = codes[1] || 'en';
  const warrantyLandingPage = i18n.t('warranty_activation').landing_page;

  const [modalShow, setModalShow] = useState(false);
  const [warrantyDetails, setWarrantyDetails] = useState({});
  const [inputErrors, setInputErrors] = useState([]);
  const [invalidError, setInvalidError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [isBtnEnabled, setIsBtnEnabled] = useState(false);
  const [formData, setFormData] = useRecoilState(warrantyInput);
  const [isLoading, setIsLoading] = useState(false);
  const clearWarrantyInput = useResetRecoilState(warrantyInput);

  const updateForm = (data) => {
    const name = Object.keys(data)[0];
    Object.keys(data).forEach((key) => {
      setFormData({ ...formData, [key]: data[key] });
    });
    const errorIndex = inputErrors.indexOf(name);
    if (errorIndex !== -1) {
      inputErrors.splice(errorIndex, 1);
      setInputErrors([...inputErrors]);
    }
  };

  const checkBtnEnabled = () => {
    let flag = true;
    Object.keys(formData).forEach((key) => {
      if (!formData[key] && !OPTIONAL_WARRANTY_FIELDS.includes(key)) {
        flag = false;
        setIsBtnEnabled(false);
      }
    });
    if (flag) {
      setIsBtnEnabled(true);
    }
  };

  const checkValidation = () => {
    const stateValidationArray = Object.keys(formData).filter(
      (key) => !OPTIONAL_WARRANTY_FIELDS.includes(key) && !formData[key]
    );
    if ((!stateValidationArray.includes('email')) && !validateEmail(formData.email)) {
      stateValidationArray.push('email');
    }
    if ((!stateValidationArray.includes('firstName')) && !isValidName(formData.firstName)) {
      stateValidationArray.push('firstName');
    }
    if (!isValidName(formData.lastName) && formData.lastName) {
      stateValidationArray.push('lastName');
    }
    return [...stateValidationArray];
  };

  const handleActivate = async() => {
    if (isLoading || !formData.serialNumber || !formData.email || !formData.firstName) return false;
    const validateInputFields = checkValidation();
    if (validateInputFields.length > 0) {
      setInputErrors([...validateInputFields]);
      return false;
    }
    warrantyActivationInitiatedClicked(countryMap[country], WARRANTY_ACTIVATION_EVENTS.HOME,
      productSerialNumberCodes[formData.serialNumber.substring(0, 4)] || null, formData.source);
    setIsLoading(true);
    const response = await validateSerialNumber(formData.serialNumber);
    if (response.data?.error || response.data?.errorCode
      || response.productWarrantyStatus === WARRANTY_ACTIVATION_STATUS.ACTIVATED) {
      if (response.data?.error?.code === WARRANTY_ACTIVATION_STATUS.INVALID_SERIAL_NUMBER) {
        setInvalidError(warrantyLandingPage.invalid_serial_number);
        setInputErrors([...inputErrors, WARRANTY_ACTIVATION_EVENTS.SERIAL_NUMBER]);
        warrantyActivationFailed(countryMap[country],
          WARRANTY_ACTIVATION_EVENTS.INVALID_SERIAL_NUMBER, formData.serialNumber);
      } else if (response.productWarrantyStatus === WARRANTY_ACTIVATION_STATUS.ACTIVATED) {
        setInvalidError(warrantyLandingPage.already_activated);
        setInputErrors([...inputErrors, WARRANTY_ACTIVATION_ERRORS.SERIAL_NUMBER]);
        warrantyActivationFailed(countryMap[country],
          WARRANTY_ACTIVATION_EVENTS.DEVICE_ALREADY_ACTIVATED, formData.serialNumber);
      } else {
        setSubmitError(warrantyLandingPage.submit_error);
      }
      setIsLoading(false);
      return false;
    }
    const details = await activateWarrantyWithSerialNumber(i18n.activeLocale, formData);
    if (details.data?.error || details.data?.errorCode) {
      switch (details.data?.error?.code) {
        case WARRANTY_ACTIVATION_STATUS.INVALID_SERIAL_NUMBER:
          setInvalidError(warrantyLandingPage.invalid_serial_number);
          setInputErrors([...inputErrors, WARRANTY_ACTIVATION_ERRORS.SERIAL_NUMBER]);
          warrantyActivationFailed(countryMap[country],
            WARRANTY_ACTIVATION_EVENTS.INVALID_SERIAL_NUMBER, formData.serialNumber);
          break;
        case WARRANTY_ACTIVATION_STATUS.NO_PENDING_WARRANTY_ACTIVATION:
          setInvalidError(warrantyLandingPage.already_activated);
          setInputErrors([...inputErrors, WARRANTY_ACTIVATION_ERRORS.SERIAL_NUMBER]);
          warrantyActivationFailed(countryMap[country],
            WARRANTY_ACTIVATION_EVENTS.DEVICE_ALREADY_ACTIVATED, formData.serialNumber);
          break;
        default: setSubmitError(warrantyLandingPage.submit_error);
      }
      setIsLoading(false);
    } else {
      setWarrantyDetails(details);
      setModalShow(true);
      clearWarrantyInput();
    }

    return false;
  };

  useEffect(() => {
    checkBtnEnabled();
  }, [formData]);

  return (
    <>
      <Head>
        {lang !== 'vi' && lang !== 'th' && lang !== 'zh' && (
        <link rel="stylesheet" href="/fonts/fonts.css" />
        )}
        {lang === 'vi' && <link rel="stylesheet" href="/fonts/fonts-vi.css" />}
        {lang === 'th' && <link rel="stylesheet" href="/fonts/fonts-th.css" />}
        {lang === 'zh' && <link rel="stylesheet" href="/fonts/fonts-zh.css" />}
        <link rel="stylesheet" href="/vendor/bootstrap/css/bootstrap.min.css" async={true} />
      </Head>
      <section>
        <div className="warranty_activation_wrapper_embed">
          <div className="main_section">
            <p
              className="warranty_description"
              data-lokalise=""
              data-key="warranty_activation::landing_page::description1"
              dangerouslySetInnerHTML={{ __html: warrantyLandingPage.description1 }}
            />
            <div className="warranty_activation_inputs">
              <InputTextField
                type="text"
                name="firstName"
                label={warrantyLandingPage.first_name_label}
                placeholder={warrantyLandingPage.first_name_placeholder}
                value={formData.firstName}
                onChange={updateForm}
                warning={inputErrors.includes('firstName') ? warrantyLandingPage.invalid_first_name : ''}
              />
              <InputTextField
                type="text"
                name="lastName"
                label={warrantyLandingPage.last_name_label}
                placeholder={warrantyLandingPage.last_name_placeholder}
                value={formData.lastName}
                onChange={updateForm}
                warning={inputErrors.includes('lastName') ? warrantyLandingPage.invalid_last_name : ''}
              />
              <InputTextField
                type="text"
                name="email"
                label={warrantyLandingPage.email_label}
                placeholder={warrantyLandingPage.email_placeholder}
                value={formData.email}
                onChange={updateForm}
                warning={inputErrors.includes('email') ? warrantyLandingPage.invalid_email : ''}
              />
              <InputTextField
                type="text"
                name="serialNumber"
                label={warrantyLandingPage.enter_serial_number}
                placeholder={warrantyLandingPage.enter_serial_number}
                value={formData.serialNumber}
                onChange={updateForm}
                warning={inputErrors.includes('serialNumber') ? invalidError : ''}
              />
              {ORDERID_REQUIRED_COUNTRIES.includes(country) && (
                <InputTextField
                  type="text"
                  name="orderId"
                  label={warrantyLandingPage.order_id}
                  placeholder={warrantyLandingPage.enter_order_id}
                  value={formData.orderId}
                  onChange={updateForm}
                  warning=""
                />)}
              <DropDownSelect
                label={warrantyLandingPage.purchase_from_label}
                placeholder={warrantyLandingPage.purchased_from}
                options={purchaseFromOptions[country]}
                value={formData.source}
                name="source"
                onChange={updateForm}
                warning=""
              />
            </div>
            {submitError && <p className="submit_error">{submitError}</p>}
            <div className="activate-btn">
              <a
                className={`web-btn web-btn--fill ${!isBtnEnabled ? 'disabled' : ''}`}
                role="presentation"
                onClick={() => handleActivate()}
                data-lokalise=""
                data-key="warranty_activation::landing_page::activate_btn"
              >
                {/* {warrantyLandingPage.activate_btn} */}
                {!isLoading ? warrantyLandingPage.activate_btn : <div className="loader" />}
              </a>
            </div>
          </div>
        </div>
      </section>
      {modalShow
      && <WarrantyActivatedModalEmbed
        setModalShow={setModalShow}
        modalShow={modalShow}
        warrantyDetails={warrantyDetails}
      />}
    </>
  );
};

export async function getStaticProps({ params }) {
  const lngDict = {};
  const pageId = PAGEID.WARRANTY_ACTIVATION;
  const keys = getTranslationKeys(pageId);
  const { default: lngDictAll = {} } = await import(
    `../../../../locales/${contentLanguageMap[params.lng]}.json`
  );
  keys.forEach((data) => {
    lngDict[data] = lngDictAll[data];
  });
  return {
    props: { lng: params.lng, lngDict, pageId }
  };
}

export async function getStaticPaths() {
  return {
    paths: languages.map((l) => ({ params: { lng: l } })),
    fallback: false
  };
}

export default WarrantyActivation;
